import type { VehicleFE } from 'domain/vehicle';
import type { SearchedVehicle } from 'features/vehicle-search/types';
import { parseSearchedVehicles } from 'features/vehicle-search/utils';
import { resolveStatusLevel } from 'helper/vehicleStatus';

interface Props {
    vehicles?: VehicleFE[];
    param?: string;
}

export type VehicleGroupKey = 'fatals' | 'errors' | 'warnings' | 'online' | 'offline';

type GroupedVehicles = Record<VehicleGroupKey, SearchedVehicle[]>;

const createEmptyGroups = (): GroupedVehicles => ({
    fatals: [],
    errors: [],
    warnings: [],
    online: [],
    offline: [],
});

/**
 * Classifies a vehicle into exactly one search-list group.
 *
 * Severity takes precedence (fatal > error > warning). Every remaining vehicle
 * is placed by connectivity: online, or otherwise offline. Expected and
 * unexpected disconnects share the single offline group so the list shows one
 * contiguous block of offline vehicles rather than two. This also guarantees no
 * vehicle can silently fall out of the list (regression guard for ACC-2468,
 * where unexpectedly-offline vehicles were dropped).
 */
export const getVehicleGroup = (vehicle: SearchedVehicle): VehicleGroupKey => {
    const level = resolveStatusLevel({
        severity: vehicle.severity,
        connectivityState: vehicle.connectivityStatus?.state,
    });

    switch (level) {
        case 'fatal':
            return 'fatals';
        case 'error':
            return 'errors';
        case 'warning':
            return 'warnings';
        case 'online':
            return 'online';
        default:
            return 'offline';
    }
};

const groupVehiclesByCriteria = (vehicles: SearchedVehicle[]): GroupedVehicles =>
    vehicles.reduce<GroupedVehicles>((groups, vehicle) => {
        groups[getVehicleGroup(vehicle)].push(vehicle);
        return groups;
    }, createEmptyGroups());

// Results are intentionally not sorted alphabetically: name and license plate are
// optional, so neither is a reliable ordering key (confirmed with design).
const matchesSearch = (vehicle: SearchedVehicle, param: string) =>
    (!!vehicle.licensePlate && vehicle.licensePlate.toLowerCase().includes(param)) ||
    (!!vehicle.name && vehicle.name.toLowerCase().includes(param)) ||
    vehicle.vin.toLowerCase().includes(param);

const useFilterVehicles = ({ vehicles, param }: Props) => {
    if (!vehicles?.length) {
        return {
            fatals: undefined,
            errors: undefined,
            warnings: undefined,
            online: undefined,
            offline: undefined,
            total: undefined,
        };
    }

    const parsedVehicles = parseSearchedVehicles(vehicles) ?? [];

    const convertedParam = param?.toLowerCase();
    const filteredVehicles = convertedParam
        ? parsedVehicles.filter(vehicle => matchesSearch(vehicle, convertedParam))
        : parsedVehicles;

    return {
        ...groupVehiclesByCriteria(filteredVehicles),
        total: filteredVehicles.length,
    };
};

export default useFilterVehicles;
